import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Upload, FileText, Download, Trash2, FileImage, FileSpreadsheet, File as FileIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: "remuneration", label: "Relevé de rémunération", description: "Fiches de paie, primes, attestations…" },
  { id: "autres", label: "Autres documents", description: "Pièce d'identité, justificatif de domicile, RIB…" },
];

// Sous-sections visibles selon (a) le rôle de la personne qui consulte et
// (b) la catégorie du collaborateur ciblé :
//  - un manager n'a JAMAIS accès à « Relevé de rémunération » (règle RBAC :
//    section réservée à l'admin — les managers ne consultent jamais leur
//    propre relevé, ils le voient via leur profil autrement).
//  - les autres rôles (admin, collaborateur/gestionnaire sur leur propre
//    dossier) ne voient la section que si le collaborateur cible est en
//    catégorie SALARIÉE (règle métier — bulletins de salaire).
const sectionsFor = (role, isSalarie) => {
  if (role === "manager") return SECTIONS.filter((s) => s.id !== "remuneration");
  if (!isSalarie) return SECTIONS.filter((s) => s.id !== "remuneration");
  return SECTIONS;
};

function fileIcon(contentType) {
  if (!contentType) return FileIcon;
  if (contentType.startsWith("image/")) return FileImage;
  if (contentType.includes("sheet") || contentType.includes("excel") || contentType.includes("csv")) return FileSpreadsheet;
  return FileText;
}

function formatSize(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
  return `${(b / (1024 * 1024)).toFixed(2)} Mo`;
}

export default function DossierDocuments({ collaborateurId, canUpload = true, canDelete = true, viewerRole = "admin", isSalarie = false }) {
  const visibleSections = sectionsFor(viewerRole, isSalarie);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [section, setSection] = useState(visibleSections[0]?.id || "autres");
  const [periodLabel, setPeriodLabel] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const fileRef = useRef(null);

  // Si la sélection courante n'est plus autorisée (ex. « remuneration » alors
  // que le collaborateur est passé en non-salarié), on retombe sur la première
  // section visible.
  useEffect(() => {
    if (!visibleSections.some((s) => s.id === section)) {
      setSection(visibleSections[0]?.id || "autres");
    }
  }, [visibleSections, section]);

  const load = async () => {
    try {
      const { data } = await api.get(`/collaborateurs/${collaborateurId}/documents`);
      setDocs(data || []);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    if (collaborateurId) load();
    // eslint-disable-next-line
  }, [collaborateurId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams({ section, title: file.name });
      if (section === "remuneration" && periodLabel) params.set("period_label", periodLabel);
      await api.post(
        `/collaborateurs/${collaborateurId}/documents?${params.toString()}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Document ajouté");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'envoi");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeDoc = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await api.delete(`/collaborateurs/${collaborateurId}/documents/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div data-testid="dossier-documents">
      <Tabs value={section} onValueChange={setSection} className="w-full">
        <TabsList className="bg-white border border-zinc-200 rounded-sm p-0 h-auto w-full sm:w-auto flex flex-wrap">
          {visibleSections.map((s) => (
            <TabsTrigger
              key={s.id}
              value={s.id}
              className="rounded-sm px-3 sm:px-4 py-2 text-xs sm:text-sm data-[state=active]:bg-zinc-100 flex-1 sm:flex-none"
              data-testid={`section-tab-${s.id}`}
            >
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {visibleSections.map((s) => {
          const filtered = docs.filter((d) => d.section === s.id);
          return (
            <TabsContent key={s.id} value={s.id} className="mt-5 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                  <p className="label-uppercase mb-1">Section</p>
                  <h3 className="font-display text-lg sm:text-xl font-semibold text-zinc-900">{s.label}</h3>
                  <p className="text-xs sm:text-sm text-zinc-600 font-mono mt-1">{s.description}</p>
                </div>
                {canUpload && (
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                    {s.id === "remuneration" && (
                      <div>
                        <p className="label-uppercase mb-1">Période</p>
                        <input
                          type="month"
                          value={periodLabel}
                          onChange={(e) => setPeriodLabel(e.target.value)}
                          className="h-9 px-3 text-sm rounded-sm border border-zinc-300 bg-white font-mono"
                          data-testid="upload-period-label"
                        />
                      </div>
                    )}
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt"
                      onChange={handleUpload}
                      className="hidden"
                      data-testid={`upload-input-${s.id}`}
                    />
                    <Button
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm w-full sm:w-auto"
                      data-testid={`upload-btn-${s.id}`}
                    >
                      <Upload size={14} className="mr-1" /> {uploading ? "Envoi…" : "Ajouter un document"}
                    </Button>
                  </div>
                )}
              </div>

              <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100" data-testid={`docs-list-${s.id}`}>
                {filtered.length === 0 ? (
                  <p className="text-sm text-zinc-500 font-mono p-6 text-center">Aucun document</p>
                ) : (
                  filtered.map((d) => {
                    const Icon = fileIcon(d.content_type);
                    return (
                      <div key={d.id} className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`doc-row-${d.id}`}>
                        <div className="h-10 w-10 bg-zinc-50 border border-zinc-200 rounded-sm flex items-center justify-center flex-shrink-0">
                          <Icon size={18} className="text-zinc-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-zinc-900 truncate">{d.title}</p>
                          <p className="text-[11px] text-zinc-500 font-mono mt-0.5 truncate">
                            {d.original_filename || ""} · {formatSize(d.size)} · {new Date(d.created_at).toLocaleDateString("fr-FR")}
                            {d.uploader_name && ` · par ${d.uploader_name}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 sm:gap-2">
                          <a
                            href={`${BACKEND_URL}/api/files/${d.storage_path}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center h-9 px-3 border border-zinc-200 text-xs hover:bg-zinc-50 rounded-sm"
                            data-testid={`download-${d.id}`}
                          >
                            <Download size={12} className="mr-1" /> Voir
                          </a>
                          {canDelete && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50 rounded-sm h-9"
                              onClick={() => removeDoc(d.id)}
                              data-testid={`delete-doc-${d.id}`}
                            >
                              <Trash2 size={12} />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
